import { isDefined, isNil } from "@clipboard-health/util-ts";
import {
  type BreakPolicy,
  ExtraWorkedTimeType,
  type TimeRange,
} from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes, parseISO } from "date-fns";

export function doesShiftHaveWorkedBreak(params: {
  breakPolicy: BreakPolicy | undefined;
  clockInOut: TimeRange;
  lunchInOut: TimeRange | undefined;
}): boolean {
  const { breakPolicy, clockInOut, lunchInOut } = params;

  if (!isDefined(breakPolicy)) {
    return false;
  }

  const workDurationInMinutes =
    isDefined(clockInOut.start) && isDefined(clockInOut.end)
      ? differenceInMinutes(parseISO(clockInOut.end), parseISO(clockInOut.start))
      : 0;
  const breakDurationInMinutes =
    isDefined(lunchInOut?.start) && isDefined(lunchInOut?.end)
      ? differenceInMinutes(parseISO(lunchInOut.end), parseISO(lunchInOut.start))
      : 0;

  return (
    workDurationInMinutes > breakPolicy.minShiftDurationInMinutes &&
    breakDurationInMinutes < breakPolicy.breakDurationInMinutes
  );
}

export interface AuthorizingEmployee {
  name: string;
  signature: string;
}

export function getExtraWorkedTime(props: {
  breakPolicy: BreakPolicy | undefined;
  clockInOut: TimeRange;
  lunchInOut: TimeRange | undefined;
  breakAuthorizationReason: string | undefined;
  authorizingEmployee?: AuthorizingEmployee;
  // When true, BREAK EWTR is attached even when the shift is evaluated as too short on the frontend, because backend requires it
  forceAttachBreakAuthorizationReason?: boolean;
}):
  | Array<{
      type: ExtraWorkedTimeType;
      authorization: { reason: string; authorizingEmployee?: AuthorizingEmployee };
    }>
  | undefined {
  const {
    breakPolicy,
    clockInOut,
    lunchInOut,
    breakAuthorizationReason,
    authorizingEmployee,
    forceAttachBreakAuthorizationReason = false,
  } = props;

  const hasReason =
    isDefined(breakAuthorizationReason) && breakAuthorizationReason.trim().length > 0;
  const shouldAttach =
    hasReason &&
    (forceAttachBreakAuthorizationReason ||
      doesShiftHaveWorkedBreak({ breakPolicy, clockInOut, lunchInOut }));

  if (!shouldAttach) {
    return undefined;
  }

  const authorization = {
    reason: breakAuthorizationReason,
    ...(authorizingEmployee ? { authorizingEmployee } : {}),
  };
  return [{ type: ExtraWorkedTimeType.BREAK, authorization }];
}

export interface MissedPunch {
  type: ExtraWorkedTimeType.FULL_SHIFT | ExtraWorkedTimeType.BREAK;
  reason: string;
}

/**
 * Constructs extraWorkedTime entries for missed punch requests.
 *
 * Returns FULL_SHIFT type with reason for missed punch requests.
 */
export function getMissedPunchExtraWorkedTime(props: {
  missedPunches: MissedPunch[] | undefined;
}): Array<{
  type: ExtraWorkedTimeType;
  authorization: { reason: string };
}> {
  const { missedPunches } = props;

  const fullShiftPunch = (missedPunches ?? []).find(
    (p) => p.type === ExtraWorkedTimeType.FULL_SHIFT
  );

  if (isNil(fullShiftPunch) || fullShiftPunch.reason.trim() === "") {
    return [];
  }

  return [
    { type: ExtraWorkedTimeType.FULL_SHIFT, authorization: { reason: fullShiftPunch.reason } },
  ];
}
